/**
 * Image Carousel Block - Frontend Script
 * 
 * Initializes Swiper carousel with responsive settings and modal functionality
 */

import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import { createBlockInit } from '../../src/js/utils/block-init.js';

/**
 * Initialize a single carousel
 * @param {HTMLElement} carouselElement - The carousel container element
 */
function initCarousel(carouselElement) {
	const blockId = carouselElement.dataset.carouselId;
	const slideCount = carouselElement.querySelectorAll('.swiper-slide').length ?? 0;

	const swiper = new Swiper(carouselElement, {
		modules: [Navigation],
		slidesPerView: 'auto',
		centeredSlides: true,
		initialSlide: slideCount ? (slideCount - 1) : 0,
		spaceBetween: 8,
		grabCursor: true,
		watchOverflow: true,
		loop: true,
		loopAdditionalSlides: 1,
		navigation: {
			nextEl: carouselElement.querySelector('.carousel-nav-next'),
			prevEl: carouselElement.querySelector('.carousel-nav-prev'),
		},
		breakpoints: {
			768: {
				spaceBetween: 16,
			},
			1024: {
				spaceBetween: 32,
			},
		},
		keyboard: {
			enabled: true,
			onlyInViewport: true,
		},
		a11y: {
			prevSlideMessage: 'Previous slide',
			nextSlideMessage: 'Next slide',
		},
		preloadImages: false,
		lazy: {
			loadPrevNext: true,
		},
		on: {
			afterInit(s) {
				s.slideToLoop(slideCount ? (slideCount - 1) : 0, 0);
			},
		},
	});
	
	initModal(carouselElement, blockId);
	
	return swiper;
}

/**
 * Initialize modal functionality for a carousel
 * @param {HTMLElement} carouselElement - The carousel container
 * @param {string} blockId - The block ID
 */
function initModal(carouselElement, blockId) {
	const modal = document.getElementById(`carousel-modal-${blockId}`);
	
	if (!modal) {
		return;
	}
	
	const modalImage = modal.querySelector('.carousel-modal__image');
	const modalCaption = modal.querySelector('.carousel-modal__caption');
	const closeButton = modal.querySelector('.carousel-modal__close');
	const imageContainers = carouselElement.querySelectorAll('.carousel-image-container');
	
	imageContainers.forEach(container => {
		const openModal = () => {
			const imageSrc = container.dataset.modalSrc;
			const imageAlt = container.querySelector('img')?.alt || '';
			const caption = container.dataset.caption;
			
			if (modalImage) {
				modalImage.src = imageSrc;
				modalImage.alt = imageAlt;
			}
			
			if (modalCaption && caption) {
				modalCaption.textContent = caption;
				modalCaption.style.display = 'block';
			} else if (modalCaption) {
				modalCaption.style.display = 'none';
			}
			
			modal.classList.add('is-active');
			document.body.style.overflow = 'hidden';
			
			setTimeout(() => closeButton?.focus(), 100);
		};
		
		container.addEventListener('click', openModal);
		
		container.addEventListener('keydown', (e) => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				openModal();
			}
		});
	});
	
	const closeModal = () => {
		modal.classList.remove('is-active');
		document.body.style.overflow = '';
		
		const activeSlide = carouselElement.querySelector('.swiper-slide-active .carousel-image-container');
		activeSlide?.focus();
	};
	
	closeButton?.addEventListener('click', closeModal);
	
	modal.addEventListener('click', (e) => {
		if (e.target === modal) {
			closeModal();
		}
	});
	
	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape' && modal.classList.contains('is-active')) {
			closeModal();
		}
	});
}

const { initAll: initImageCarousels } = createBlockInit('.image-carousel-swiper', initCarousel);

export { initImageCarousels, initCarousel };
